import { FC, ReactElement, useMemo } from 'react'
import { CryptoRewardsPanePreset } from 'components/features/rewards/rewardsPane/components/CryptoRewardsPanePreset/CryptoRewardsPanePreset'
import { getSellOrderSuccessResultOrNullSelector } from 'components/features/rewards/rewardsPane/store/selectors/orderState'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import {
  CryptoRewardsOrderSuccessResultWarning,
} from 'components/features/rewards/rewardsPane/components/CryptoRewardsOrderSuccessResultWarning/CryptoRewardsOrderSuccessResultWarning'
import { CryptoCurrencyLogo } from 'components/features/rewards/rewardsPane/containers/CryptoCurrencyLogo/CryptoCurrencyLogo'
import { getCryptoRewardsPaneProgressPercentsStateSelector } from 'components/features/rewards/rewardsPane/store/selectors/paneState'
import { FLOATING_PANE_CRYPTO_REWARDS_SELL_MODE } from 'components/features/rewards/rewardsPane/constants/paneState'
import { OrderSuccessResultWithMeta } from 'components/features/rewards/rewardsPane/types/orderResult'
import SvgIcon from 'components/atoms/SvgIcon'
import BackgroundSvg from 'icons/crypto_sell_portal.svg?skip-svgo'

import styles from 'components/features/rewards/rewardsPane/containers/CryptoRewardsSellPane/CryptoRewardsSellSuccessStep/CryptoRewardsSellSuccessStep.styl'

type CryptoRewardsSellSuccessStepProps = {
  onClose: () => void
}

type CryptoRewardsSellSuccessProps = CryptoRewardsSellSuccessStepProps & {
  sellOrderResult: OrderSuccessResultWithMeta<typeof FLOATING_PANE_CRYPTO_REWARDS_SELL_MODE>
}

export const CryptoRewardsSellSuccess: FC<CryptoRewardsSellSuccessProps> = ({ sellOrderResult, onClose }): ReactElement => {
  const paneProgressPercent = useStoreSelector(getCryptoRewardsPaneProgressPercentsStateSelector)
  const { currencyId } = sellOrderResult

  const successInvestmentIcon = useMemo<ReactElement>(() => {
    const cryptoCurrencyLogo = <CryptoCurrencyLogo currencyId={ currencyId } className={ styles.currencyIcon } />
    const backgroundImage = <SvgIcon src={ BackgroundSvg } useDefaultViewBox />

    return (
      <div className={ styles.successBannerWrapper }>
        <div className={ styles.currencyIconWrapper }>
          { cryptoCurrencyLogo }
        </div>
        { backgroundImage }
      </div>
    )
  }, [currencyId])
  const notice = useMemo<ReactElement>(() => {
    return (
      <div className={ styles.notice }>
        We’ll let you know as soon as the order goes through. You can track the order on your rewards page.
      </div>
    )
  }, [])
  const successResultHeader = useMemo(() => (
    <CryptoRewardsOrderSuccessResultWarning
      renderMode={ FLOATING_PANE_CRYPTO_REWARDS_SELL_MODE }
      orderSuccessResult={ sellOrderResult }
    />
  ), [sellOrderResult])

  return (
    <CryptoRewardsPanePreset
      title='Sell Your Crypto Investments'
      subTitle='Success!'
      actionName='Close'
      withFooterCancel={ false }
      onCancel={ onClose }
      onSuccess={ onClose }
      progressBarPercent={ paneProgressPercent }
    >
      { successResultHeader }
      { successInvestmentIcon }
      { notice }
    </CryptoRewardsPanePreset>
  )
}

export const CryptoRewardsSellSuccessStep: FC<CryptoRewardsSellSuccessStepProps> = ({ onClose }): ReactElement | null => {
  const sellOrderResult = useStoreSelector(getSellOrderSuccessResultOrNullSelector)

  if (!sellOrderResult) {
    return null
  }
  return <CryptoRewardsSellSuccess sellOrderResult={ sellOrderResult } onClose={ onClose } />
}
